import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllOrders, getAllEngagements } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import { Plus, Eye } from 'lucide-react';
import Header from '../../components/layout/Header';
import Badge, { statusToBadge } from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import OrderDetailDrawer, { ORDER } from '../../components/orders/OrderDetailDrawer';
import CreateOrderForm from '../../components/orders/CreateOrderForm';
import { ORDER_STATUS, formatDate, formatINR, label, options } from '../../constants/garmentCodes';

const SUMMARY = [
  { key: 'production', text: 'In Production', match: [ORDER.CONFIRMED, ORDER.IN_PRODUCTION, ORDER.READY], cls: 'bg-[#FEF3E2] border-[#F7C97A] text-[#92560C]' },
  { key: 'estimate', text: 'Estimate to Review', match: [ORDER.ESTIMATE_PENDING], cls: 'bg-[#FEF3E2] border-[#F7C97A] text-[#92560C]' },
  { key: 'dispatched', text: 'Dispatched', match: [ORDER.DISPATCHED], cls: 'bg-[#EEF0FB] border-[#B8C1E8] text-[#3D52A0]' },
  { key: 'delivered', text: 'Delivered', match: [ORDER.DELIVERED], cls: 'bg-[#E8F5EE] border-[#A8DEC0] text-[#1A6B3E]' },
];

export default function BrandOrders() {
  const dispatch = useDispatch();
  const orders = useSelector((state) => state.order.orders);
  const loading = useSelector((state) => state.order.loading);
  const engagements = useSelector((state) => state.engagement.engagements);
  const brandId = useSelector((state) => state.brand.profile?.id);
  const engagementMap = useMemo(() => Object.fromEntries(engagements.map((e) => [String(e.id), e])), [engagements]);
  const [selectedId, setSelectedId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllEngagements({ brandId }));
  }, [dispatch, brandId]);

  useEffect(() => {
    dispatch(getAllOrders({ brandId, status: '' }));
  }, [dispatch, brandId]);

  const counts = orders.reduce((acc, o) => {
    SUMMARY.forEach((s) => {
      if (s.match.includes(o.status)) acc[s.key] = (acc[s.key] || 0) + 1;
    });
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header
        title="Orders"
        subtitle={`${orders.length} orders`}
        actions={<Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setAddOpen(true)}>Place order</Button>}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {SUMMARY.map((s) => (
            <div key={s.key} className={`rounded-xl border p-4 ${s.cls}`}>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{counts[s.key] || 0}</p>
              <p className="text-xs font-medium mt-1 opacity-80">{s.text}</p>
            </div>
          ))}
          <div className="rounded-xl border p-4 bg-white border-[#E5E3DE]">
            <p className="text-2xl font-bold text-[#1A1915]" style={{ fontFamily: 'Manrope, sans-serif' }}>{orders.length}</p>
            <p className="text-xs font-medium text-[#9B9893] mt-1">Total orders</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {loading && <span className="text-xs text-[#9B9893]">Loading…</span>}
        </div>

        <div className="bg-white rounded-xl border border-[#E5E3DE] overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="BrandOrders"
            columns={[
              { id: 'orderId', label: 'ORDER', align: 'left' },
              { id: 'product', label: 'PRODUCT', align: 'left' },
              { id: 'manufacturer', label: 'MANUFACTURER', align: 'left' },
              { id: 'qty', label: 'QTY', align: 'left' },
              { id: 'estimate', label: 'ESTIMATE', align: 'left' },
              { id: 'walletHold', label: 'WALLET HOLD', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'delivery', label: 'DELIVERY', align: 'left' },
              { id: 'action', label: '', align: 'center', disableFilter: true },
            ]}
            rows={orders.map((order) => {
              const status = label(ORDER_STATUS, order.status);
              const needsAction = order.status === ORDER.ESTIMATE_PENDING || order.status === ORDER.DISPATCHED;
              return {
                id: order.id,
                rColumns: [
                  <div key="id" label={order.orderNumber || ''} className={`flex items-center w-full h-full px-2 py-1 font-mono text-xs text-[#3D52A0] font-semibold ${order.__optimistic ? 'opacity-60' : ''}`}>{order.orderNumber || `#${order.id}`}</div>,
                  <div key="prod" label={order.product || ''} className="flex flex-col justify-center items-start w-full h-full px-2 py-1">
                    <p className="text-sm font-medium text-[#1A1915]">{order.product}</p>
                    <p className="text-xs text-[#9B9893]">{[order.designName, order.fabricName].filter(Boolean).join(' · ')}</p>
                  </div>,
                  <div key="mfg" label={engagementMap[String(order.engagementId)]?.manufacturerName || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{engagementMap[String(order.engagementId)]?.manufacturerName || '—'}</div>,
                  <div key="qty" label={String(order.totalQty ?? '')} className="flex items-center w-full h-full px-2 py-1 text-xs font-medium">{order.totalQty?.toLocaleString('en-IN') ?? '—'}</div>,
                  <div key="est" label={formatINR(order.totalEstimate)} className="flex items-center w-full h-full px-2 py-1 text-xs font-semibold text-[#1A1915]">{order.totalEstimate ? formatINR(order.totalEstimate) : '—'}</div>,
                  <div key="hold" label={formatINR(order.walletHold)} className="flex items-center w-full h-full px-2 py-1 text-xs font-medium text-[#92560C]">{order.walletHold ? formatINR(order.walletHold) : '—'}</div>,
                  <div key="stat" label={status} className="flex items-center w-full h-full px-2 py-1"><Badge variant={statusToBadge(status)}>{status}</Badge></div>,
                  <div key="del" label={order.targetDelivery || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{formatDate(order.targetDelivery)}</div>,
                  <div key="act" label="" className="flex items-center justify-center w-full h-full px-2 py-1">
                    {!order.__optimistic && (
                      <Button variant={needsAction ? 'primary' : 'ghost'} size="xs" icon={<Eye size={11} />} onClick={() => setSelectedId(order.id)}>
                        {order.status === ORDER.ESTIMATE_PENDING ? 'Review estimate' : order.status === ORDER.DISPATCHED ? 'Confirm receipt' : 'View'}
                      </Button>
                    )}
                  </div>,
                ],
              };
            })}
          />
          {!loading && orders.length === 0 && <p className="py-10 text-center text-sm text-[#9B9893]">No orders yet</p>}
        </div>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Place Production Order" subtitle="The manufacturer prepares a cost estimate; a wallet hold is placed when you approve it." size="lg">
        <CreateOrderForm engagements={engagements} initialStatus={ORDER.SUBMITTED} onDone={() => setAddOpen(false)} />
      </Modal>

      <OrderDetailDrawer orderId={selectedId} role="brand" onClose={() => setSelectedId(null)} />
    </div>
  );
}
